// Top toolbar row — Ecranify brand logo, project name, help, save
// Sits above SceneToolbar in the editor layout.

import {useState, useRef, useMemo, useCallback} from 'react'
import {useParams, Link as RouterLink} from 'react-router-dom'
import {
  IconButton,
  Box,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  TextField,
  useMediaQuery,
} from '@mui/material'
import SaveRounded from '@mui/icons-material/SaveRounded'
import FileDownloadRounded from '@mui/icons-material/FileDownloadRounded'
import CollectionsRounded from '@mui/icons-material/CollectionsRounded'
import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded'
import EditRounded from '@mui/icons-material/EditRounded'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, store} from '../../../common/store'
import {useEditorConfig} from '../../../common/hooks/useEditorConfig'
import {updateMyProject} from '../../../common/api/generated/endpoints/my-projects/my-projects'
import {useToast} from '../../../common/components/toast/toastContext'
import {handleApiError} from '../../../common/errors/handleApiError'
import {addBreadcrumb} from '../../../common/errors/breadcrumb'
import {trackEvent} from '../../../common/analytics/trackEvent'
import {getAssetsDirHandle} from '../../../common/utils/devAssetWriter'
import {writeDevicePreset, writeProjectPreset} from './writePreset'
import SavePresetDialog from './SavePresetDialog'
import type {PresetMeta} from './SavePresetDialog'
import {
  setProjectName as setGlobalProjectName,
  getProjectName,
  useProjectName,
} from '../../../common/components/store/projectNameStore'
import {getProjectId} from '../../../common/components/store/projectIdStore'
import {saveProject} from './saveProjectFn'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import {MOBILE_BREAKPOINT} from '../constants/responsive'
import MobileSceneSelect from './MobileSceneSelect'
import BrandLogo from '../BrandLogo'
import LoadingLogo from '../LoadingLogo'

// ── EditorTopBar ──────────────────────────────────────────────────

interface EditorTopBarProps {
  // When true, hides the project name and save controls (used by FreeEditorPage).
  freeMode?: boolean
}

const EditorTopBar = ({freeMode = false}: EditorTopBarProps) => {
  const dispatch = useAppDispatch()
  const tokens = useAppTokens()
  const {t} = useTranslation()
  const toast = useToast()
  const {deviceConfig} = useEditorConfig()
  const {projectId, projectSlug} = useParams()
  const liveProjectName = useProjectName()
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT)
  const logoTo = freeMode ? '/' : '/my-projects'

  // Save state
  const [saving, setSaving] = useState(false)
  const [nameDialogOpen, setNameDialogOpen] = useState(false)
  const [projectNameInput, setProjectNameInput] = useState('')
  const [renameDialogOpen, setRenameDialogOpen] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [renaming, setRenaming] = useState(false)

  // Dev-only preset authoring (writes into the repo assets/ folder)
  const [presetKind, setPresetKind] = useState<'device' | 'project' | null>(null)
  const pendingPresetAfterNameRef = useRef(false)

  // ── Save handler ────────────────────────────────────────────────

  const doSave = useCallback(
    async (name?: string) => {
      setSaving(true)
      addBreadcrumb('user-action', 'save-project', 'info', {hasName: !!name})
      // Single source of truth for create-vs-update: the shared projectIdStore (set by
      // saveProjectFn on create and by EditorRoute on load). Reading a local ref here
      // would miss an id created by the "save before Export" gate → a second POST →
      // a duplicate project. Route param is the bootstrap value before the store fills.
      const currentId = getProjectId() ?? projectId
      const isFirstSave = !currentId || currentId === 'new'
      try {
        await saveProject({
          dispatch,
          deviceConfig,
          existingId: currentId,
          name,
          forkedFromSlug: projectSlug,
        })
        trackEvent('project_saved', {is_first_save: isFirstSave})
        toast.success(t('toolbar.saveSuccess'))
      } catch (err) {
        toast.error(handleApiError(err, 'saveProject') ?? t('toolbar.saveFailed'))
      } finally {
        setSaving(false)
      }
    },
    [dispatch, deviceConfig, projectId, projectSlug, t, toast],
  )

  const handleSave = useCallback(() => {
    const existingId = getProjectId() ?? projectId
    if (existingId && existingId !== 'new') {
      doSave()
    } else if (liveProjectName) {
      doSave(liveProjectName)
    } else {
      setProjectNameInput('')
      setNameDialogOpen(true)
    }
  }, [doSave, liveProjectName, projectId])

  const handleConfirmSave = useCallback(async () => {
    setNameDialogOpen(false)
    await doSave(projectNameInput)
    // If naming was triggered by the project-preset button, continue to the preset modal.
    if (pendingPresetAfterNameRef.current) {
      pendingPresetAfterNameRef.current = false
      setPresetKind('project')
    }
  }, [doSave, projectNameInput])

  const handleCancelSave = useCallback(() => {
    pendingPresetAfterNameRef.current = false
    setNameDialogOpen(false)
  }, [])

  const handleHelp = useCallback(() => {
    // Placeholder — wire later
  }, [])

  // ── Rename handlers ─────────────────────────────────────────────

  const handleOpenRename = useCallback(() => {
    setRenameValue(liveProjectName ?? '')
    setRenameDialogOpen(true)
  }, [liveProjectName])

  const handleCancelRename = useCallback(() => {
    setRenameDialogOpen(false)
  }, [])

  const handleConfirmRename = useCallback(async () => {
    const trimmed = renameValue.trim()
    if (!trimmed) return
    const existingId = getProjectId() ?? projectId
    if (existingId && existingId !== 'new') {
      setRenaming(true)
      addBreadcrumb('user-action', 'rename-project', 'info', {projectId: existingId})
      try {
        await updateMyProject(existingId, {name: trimmed})
        setGlobalProjectName(trimmed)
        setRenameDialogOpen(false)
        trackEvent('project_renamed')
      } catch (err) {
        toast.error(handleApiError(err, 'renameProject') ?? t('toolbar.renameFailed'))
      } finally {
        setRenaming(false)
      }
    } else {
      setGlobalProjectName(trimmed)
      setRenameDialogOpen(false)
    }
  }, [renameValue, projectId, t, toast])

  // Dev-only preset authoring. Both buttons open SavePresetDialog (free/paid + project
  // metadata) and then write straight into the repo assets/ folder via the File System
  // Access API — devices → assets/devices/{tier}/<slug>.json, projects → all scenes to
  // assets/projects/{tier}/<slug>.json (backgrounds/decals bundled, screen images stripped).
  const handleSaveDevice = useCallback(() => {
    setPresetKind('device')
  }, [])

  // Force a save first so the project has a name (→ slug) and all pending data-URL
  // assets are uploaded (→ resolvable URLs to bundle), then open the preset modal.
  const handleSaveProjectPreset = useCallback(async () => {
    const name = getProjectName()
    if (!name) {
      setProjectNameInput('')
      pendingPresetAfterNameRef.current = true
      setNameDialogOpen(true)
      return
    }
    await doSave(name)
    setPresetKind('project')
  }, [doSave])

  const handlePresetConfirm = useCallback(
    async (meta: PresetMeta) => {
      const kind = presetKind
      setPresetKind(null)
      try {
        const root = await getAssetsDirHandle()
        if (kind === 'device') {
          const state = store.getState().present
          const scene = state.scenes.scenes[state.scenes.activeSceneIndex]
          if (!scene) return
          await writeDevicePreset(root, scene, deviceConfig, meta.tier)
        } else if (kind === 'project') {
          const name = getProjectName()
          if (!name) return
          await writeProjectPreset(root, store.getState().present, deviceConfig, name, meta)
        }
        toast.success(t('toolbar.presetSaved'))
      } catch (err) {
        toast.error(err instanceof Error ? err.message : t('toolbar.savePresetFailed'))
      }
    },
    [presetKind, deviceConfig, t, toast],
  )

  const handlePresetCancel = useCallback(() => {
    setPresetKind(null)
  }, [])

  const sx = useMemo(() => buildStyles(tokens), [tokens])
  const displayName = liveProjectName ?? t('topBar.untitledProject')

  return (
    <>
      <Box sx={sx.root}>
        <Box component={RouterLink} to={logoTo} sx={sx.logoLink}>
          <BrandLogo markSize={24} fontSize={14} gap={8} />
        </Box>
        {!freeMode && !isMobile && (
          <Box sx={sx.projectNameWrap}>
            <Typography sx={sx.projectName}>{displayName}</Typography>
            <Tooltip title={t('topBar.renameProject')} placement="bottom">
              <IconButton
                size="small"
                onClick={handleOpenRename}
                sx={sx.renameProjectBtn}
                aria-label={t('topBar.renameProject')}
              >
                <EditRounded sx={sx.renameProjectIcon} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        {!freeMode && isMobile && <MobileSceneSelect />}
        <Box sx={sx.spacer} />
        <Tooltip title={t('topBar.help')} placement="bottom">
          <IconButton size="small" onClick={handleHelp} sx={sx.iconBtn}>
            <HelpOutlineRounded sx={sx.icon} />
          </IconButton>
        </Tooltip>
        {!freeMode && import.meta.env.DEV && (
          <>
            <Tooltip title={t('toolbar.saveDevice')} placement="bottom">
              <IconButton size="small" onClick={handleSaveDevice} sx={sx.saveDeviceBtn}>
                <FileDownloadRounded sx={sx.saveIcon} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('toolbar.saveProjectPreset')} placement="bottom">
              <IconButton size="small" onClick={handleSaveProjectPreset} sx={sx.saveProjectPresetBtn}>
                <CollectionsRounded sx={sx.saveIcon} />
              </IconButton>
            </Tooltip>
          </>
        )}
        {!freeMode && (
          <Tooltip title={t('toolbar.save')} placement="bottom">
            <IconButton size="small" onClick={handleSave} disabled={saving} sx={sx.saveBtn}>
              {saving ? <LoadingLogo size={16} /> : <SaveRounded sx={sx.saveIcon} />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Save project name dialog */}
      <Dialog open={nameDialogOpen} onClose={handleCancelSave}>
        <DialogTitle sx={sx.dialogTitle}>{t('toolbar.saveProject')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder={t('toolbar.projectNamePlaceholder')}
            value={projectNameInput}
            onChange={(e) => setProjectNameInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && projectNameInput.trim()) handleConfirmSave()
            }}
            sx={{mt: 1}}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSave}>{t('toolbar.cancel')}</Button>
          <Button onClick={handleConfirmSave} variant="contained" disabled={!projectNameInput.trim()}>
            {t('toolbar.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename project dialog */}
      <Dialog open={renameDialogOpen} onClose={handleCancelRename}>
        <DialogTitle sx={sx.dialogTitle}>{t('topBar.renameProject')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder={t('toolbar.projectNamePlaceholder')}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && renameValue.trim()) handleConfirmRename()
            }}
            sx={{mt: 1}}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRename}>{t('toolbar.cancel')}</Button>
          <Button onClick={handleConfirmRename} variant="contained" disabled={!renameValue.trim() || renaming}>
            {t('toolbar.rename')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dev-only preset metadata dialog (free/paid + project tags/description) */}
      <SavePresetDialog
        key={presetKind ?? 'closed'}
        open={presetKind !== null}
        kind={presetKind ?? 'device'}
        onConfirm={handlePresetConfirm}
        onCancel={handlePresetCancel}
      />
    </>
  )
}

// ── Styles ────────────────────────────────────────────────────────

const buildStyles = (tokens: ThemeTokens) => ({
  root: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '0 12px',
    minHeight: 40,
    backgroundColor: tokens.sidebar.background,
    borderBottom: `1px solid ${tokens.sidebar.border}`,
    flexShrink: 0,
  },
  logoLink: {
    display: 'inline-flex',
    alignItems: 'center',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'filter 0.15s, opacity 0.15s',
    '&:hover': {
      filter: 'brightness(1.15)',
    },
  },
  projectNameWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    minWidth: 0,
  },
  projectName: {
    fontSize: 13,
    fontWeight: 500,
    color: tokens.text.secondary,
    userSelect: 'none' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    maxWidth: 320,
  },
  renameProjectBtn: {
    padding: '2px',
    color: tokens.text.muted,
    opacity: 0.5,
    '&:hover': {
      opacity: 1,
      color: tokens.text.primary,
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  renameProjectIcon: {
    fontSize: 14,
  },
  spacer: {
    flex: 1,
  },
  iconBtn: {
    color: tokens.text.muted,
    padding: '4px',
    '&:hover': {
      color: tokens.text.primary,
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
    },
  },
  icon: {
    fontSize: 18,
  },
  saveBtn: {
    color: '#ffffff',
    backgroundColor: tokens.accent.primary,
    borderRadius: '6px',
    padding: '6px 10px',
    border: `1px solid ${tokens.accent.dark}`,
    boxShadow: `0 1px 8px ${tokens.accent.glow}`,
    '&:hover': {
      backgroundColor: tokens.accent.light,
      borderColor: tokens.accent.primary,
    },
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.6)',
      backgroundColor: tokens.accent.dark,
    },
  },
  saveDeviceBtn: {
    color: '#ffffff',
    backgroundColor: '#d32f2f',
    borderRadius: '6px',
    padding: '6px 10px',
    border: '1px solid #8b1d1d',
    boxShadow: '0 1px 8px rgba(211, 47, 47, 0.5)',
    '&:hover': {
      backgroundColor: '#e84545',
      borderColor: '#d32f2f',
    },
  },
  saveProjectPresetBtn: {
    color: '#ffffff',
    backgroundColor: '#7b1fa2',
    borderRadius: '6px',
    padding: '6px 10px',
    border: '1px solid #4a148c',
    boxShadow: '0 1px 8px rgba(123, 31, 162, 0.5)',
    '&:hover': {
      backgroundColor: '#9c27b0',
      borderColor: '#7b1fa2',
    },
  },
  saveIcon: {
    fontSize: 18,
  },
  dialogTitle: {
    fontSize: 16,
    fontWeight: 600,
  },
})

export default EditorTopBar
